// src/hooks/useParentChannelStatus.js
//
// Given the team's players, resolve each parent's reachability state
// by looking up the matching user-doc by phoneNumber (and falling
// back to email). Batches the queries by chunks of 10 (Firestore `in`
// limit for a where clause) so a typical 15-30 player team is 1-3
// reads for user-doc resolution, plus up to N parallel sub-reads to
// check the fcmTokens subcollection per resolved parent.
//
// User-doc field canonicals (verified by reading ProfileSettings,
// ParentOnboarding, parentPortal CF, and NotificationSettings):
//   • phoneNumber  — verified phone (E.164). NOT `phone` (legacy).
//   • email        — auth email, lowercased on write.
//   • smsOptIn     — sometimes mirrored on user-doc; canonical home
//                    is `users/{uid}/notificationSettings/default`
//                    (owner-only). We OR with `compliance.smsConsent`
//                    which is the user-doc fallback written when the
//                    consent CF fails.
//
// FCM token storage shape (per NotificationContext.jsx:137 +
// usePushNotifications.js native bridge):
//   `users/{uid}/fcmTokens/{tokenId}` — a SUBCOLLECTION, not a field
//   on the parent user-doc. So "has the app" means "this subcollection
//   contains at least 1 doc."
//
// SCOPE LIMITATION: this hook reads other-user docs + their
// fcmTokens subcollections. firestore.rules restrict the user-doc
// to `isOwner || isPlatform || isAdmin`, and the subcollection to
// OWNER-ONLY. So the hook works correctly for FOUNDERS (Kurt) but
// will return empty/no-token data for regular coach accounts.
// Migrating to a server-side callable (`getRosterChannelStatus`)
// is the right path before this UI ships to non-founder coaches.

import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '@/firebase/firebase';

// Mirrors functions/core/phone.js toE164US — keep in sync if either
// changes. Simple US-centric normalizer.
function toE164US(input) {
  if (input == null) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  if (/^\+1\d{10}$/.test(raw)) return raw;
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function tsToMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.toDate === 'function')   return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  return Number(ts) || 0;
}

export default function useParentChannelStatus(players) {
  const [byPhone, setByPhone] = useState({});  // E.164 → { fcmToken, smsOptIn, lastSeenMs }
  const [byEmail, setByEmail] = useState({});  // lowercased → same
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // Extract unique parent phones + emails from the player list.
  const { phones, emails } = useMemo(() => {
    const phoneSet = new Set();
    const emailSet = new Set();
    for (const p of players || []) {
      const e164 = toE164US(p?.parentPhone);
      if (e164) phoneSet.add(e164);
      const guardEmail = p?.guardians?.[0]?.email;
      const email = (guardEmail || p?.parentEmail || '').trim().toLowerCase();
      if (email && EMAIL_RE.test(email)) emailSet.add(email);
    }
    return { phones: [...phoneSet], emails: [...emailSet] };
  }, [players]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (phones.length === 0 && emails.length === 0) {
        setByPhone({});
        setByEmail({});
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const phoneMap = {};
        const emailMap = {};

        // Phone batches — Firestore `in` operator caps at 10 values.
        // User-doc field is `phoneNumber` (per ProfileSettings.jsx + the
        // ParentOnboarding verify flow). The legacy `phone` field is not
        // canonical; never query it.
        for (const batch of chunk(phones, 10)) {
          const q = query(collection(db, 'users'), where('phoneNumber', 'in', batch));
          const snap = await getDocs(q);
          snap.docs.forEach((d) => {
            const u = d.data() || {};
            // `smsOptIn` canonical home is `users/{uid}/notificationSettings/default`
            // (owner-only subcollection). The user-doc sometimes carries a
            // mirrored flag (`smsOptIn` or `compliance.smsConsent`) but is
            // not guaranteed. Treat phoneVerified+presence as the strongest
            // signal we can read coach-side without the subcollection.
            phoneMap[u.phoneNumber] = {
              smsOptIn: u.smsOptIn === true || u?.compliance?.smsConsent === true,
              lastSeenMs: Math.max(
                tsToMs(u.presence?.lastSeenAt),
                tsToMs(u.lastActive),
              ),
              uid:        d.id,
              email:      (u.email || '').toLowerCase(),
              hasFcmToken: false,  // filled in below by subcollection check
            };
          });
        }

        // Email batches — only for parents whose phone didn't match.
        const unmatchedEmails = emails.filter((e) => {
          return !Object.values(phoneMap).some((v) => v?.email === e);
        });
        for (const batch of chunk(unmatchedEmails, 10)) {
          const q = query(collection(db, 'users'), where('email', 'in', batch));
          const snap = await getDocs(q);
          snap.docs.forEach((d) => {
            const u = d.data() || {};
            const k = (u.email || '').toLowerCase();
            if (!k) return;
            emailMap[k] = {
              smsOptIn: u.smsOptIn === true || u?.compliance?.smsConsent === true,
              lastSeenMs: Math.max(
                tsToMs(u.presence?.lastSeenAt),
                tsToMs(u.lastActive),
              ),
              uid:        d.id,
              hasFcmToken: false,
            };
          });
        }

        // FCM token check — for each resolved user, check whether
        // their `users/{uid}/fcmTokens` subcollection has at least
        // one doc. Tokens are stored as separate docs, not as a
        // single field. Fan out in parallel; for a 30-player team
        // this is ~15-25 round-trips concurrent, ~200ms wall-clock.
        // Rules permit this read for the doc's owner only — so this
        // ONLY returns data when the calling user is the founder
        // (platform-admin claim). Regular coaches will see no
        // installed-app indicators. See module-header note.
        const allResolved = [
          ...Object.values(phoneMap),
          ...Object.values(emailMap),
        ];
        const uniqueUids = [...new Set(allResolved.map((v) => v.uid).filter(Boolean))];
        const fcmByUid = {};
        await Promise.all(uniqueUids.map(async (uid) => {
          try {
            const snap = await getDocs(
              query(collection(db, 'users', uid, 'fcmTokens'), limit(1)),
            );
            fcmByUid[uid] = snap.size > 0;
          } catch {
            // Permission-denied (likely non-founder caller). Mark
            // unknown — UI degrades to "dashed gray" badge.
            fcmByUid[uid] = false;
          }
        }));
        for (const v of allResolved) {
          if (v.uid && fcmByUid[v.uid] != null) v.hasFcmToken = fcmByUid[v.uid];
        }

        if (!cancelled) {
          setByPhone(phoneMap);
          setByEmail(emailMap);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'channel-status lookup failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [phones.join('|'), emails.join('|')]);

  // Resolver function — given a player, return its channel state.
  function lookup(player) {
    const e164 = toE164US(player?.parentPhone);
    const guardEmail = player?.guardians?.[0]?.email;
    const email = (guardEmail || player?.parentEmail || '').trim().toLowerCase();

    const phoneHit = e164 ? byPhone[e164] : null;
    const emailHit = email ? byEmail[email] : null;
    const userHit  = phoneHit || emailHit;

    const phoneValid = !!e164;
    const emailValid = !!email && EMAIL_RE.test(email);

    return {
      // Push state — true when the parent's user-doc has at least
      // one fcmToken subcollection doc. Note: only resolves for
      // founder-claim callers due to subcollection rules; coach
      // callers will see hasPush=false even when the parent does
      // have the app installed.
      hasPush:     !!userHit?.hasFcmToken,
      pushUnknown: !userHit,                     // no user-doc resolved
      // SMS state
      hasSms:         !!userHit?.smsOptIn && phoneValid,
      hasPhoneNoOptIn: !userHit?.smsOptIn && phoneValid && !!userHit,
      smsUnknown:      !userHit && phoneValid,   // has phone but no user-doc
      // Email state
      hasEmail: emailValid,
      // Debug-handy
      uid:        userHit?.uid || null,
      lastSeenMs: userHit?.lastSeenMs || 0,
    };
  }

  return { lookup, loading, error };
}
